import React from "react";
import PropTypes from "prop-types";
import ReactApexChart from "react-apexcharts";
import { EmptyState } from "../StatusStates";

/**
 * 共用圖表元件（主計劃：複用圖表模板；單元A 企業級圖表庫）。
 *
 * 統一封裝 ApexCharts，報表圖表 17 張與看板 widgets 只換 type/data，不各自刻。
 * 第二階段 10 萬點等重負載場景改用 ECharts（已在依賴），介面維持一致。
 *
 *   <Chart type="bar"  series={[{name:"數量",data:[3,5,2]}]} categories={["A","B","C"]} />
 *   <Chart type="line" series={[{name:"U使用",data:[...]}]} categories={months} />
 *   <Chart type="donut" series={[40,30,30]} categories={["嚴重","高","中"]} />
 */

// UI Style 色票(Primary 紫 + Bootstrap 5 原值:green/yellow/red/blue/secondary/teal)
const PALETTE = ["#7749f8", "#28a745", "#ffc107", "#dc3545", "#0d6efd", "#6c757d", "#20c997"];

function buildOptions(type, { categories, colors, horizontal, stacked, title, dataLabels }) {
  const base = {
    chart: {
      type,
      stacked: !!stacked,
      toolbar: { show: false },
    },
    colors: colors || PALETTE,
    dataLabels: { enabled: !!dataLabels },
    legend: { position: "bottom" },
    grid: { borderColor: "#f1f1f1" },
  };
  // 僅在有值時才加 title；避免 title:undefined 讓 ApexCharts 讀 config.title.text 出錯
  if (title) base.title = { text: title, style: { fontSize: "14px" } };

  if (["pie", "donut", "radialBar", "polarArea"].includes(type)) {
    return { ...base, labels: categories || [] };
  }
  return {
    ...base,
    plotOptions: { bar: { horizontal: !!horizontal, borderRadius: 4, columnWidth: "55%" } },
    stroke: { curve: "smooth", width: type === "line" ? 2 : 1 },
    fill: type === "area" ? { type: "gradient", gradient: { opacityFrom: 0.4, opacityTo: 0.1 } } : {},
    xaxis: { categories: categories || [] },
  };
}

const isEmptySeries = (series) => {
  if (!series || series.length === 0) return true;
  // 數值陣列（pie/donut）
  if (typeof series[0] === "number") return series.every((n) => !n);
  // [{data:[]}] 型
  return series.every((s) => !s.data || s.data.length === 0);
};

const Chart = ({
  type = "bar",
  series,
  categories = [],
  height = 300,
  colors,
  horizontal = false,
  stacked = false,
  title,
  dataLabels = false,
  emptyText = "尚無資料",
}) => {
  if (isEmptySeries(series)) {
    return <EmptyState title={emptyText} hint="資料到位後將自動呈現圖表。" />;
  }
  const options = buildOptions(type, { categories, colors, horizontal, stacked, title, dataLabels });
  return (
    <ReactApexChart
      options={options}
      series={series}
      type={type}
      height={height}
    />
  );
};

Chart.propTypes = {
  type: PropTypes.oneOf(["bar", "line", "area", "pie", "donut", "radialBar", "polarArea"]),
  series: PropTypes.array,
  categories: PropTypes.array,
  height: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  colors: PropTypes.array,
  horizontal: PropTypes.bool,
  stacked: PropTypes.bool,
  title: PropTypes.string,
  dataLabels: PropTypes.bool,
  emptyText: PropTypes.string,
};

export default Chart;
